import React, { useEffect } from 'react';
import { AlertTriangleIcon, InfoIcon, ShieldIcon, ClockIcon, CheckCircleIcon } from 'lucide-react';
type LimitStatus = 'pass' | 'warn' | 'watch';
interface LimitRow {
  label: string;
  value: string;
  status: LimitStatus;
  note?: string;
}
interface ExposureRow {
  name: string;
  type: 'Obligor' | 'Sector';
  pct: number;
  limit: number;
  status: LimitStatus;
}
interface AlertRow {
  time: string;
  level: 'info' | 'warn' | 'breach';
  message: string;
}
interface ActionRow {
  time: string;
  actor: string;
  action: string;
  result: string;
}
const LIMITS: LimitRow[] = [{
  label: 'Obligor concentration < 20%',
  value: '18.2%',
  status: 'warn',
  note: 'Walmart trending higher than target band'
}, {
  label: 'Top 5 concentration < 60%',
  value: '52.8%',
  status: 'watch'
}, {
  label: 'Sector limit < 35%',
  value: '31.0%',
  status: 'warn',
  note: 'Retail exposure building across anchors'
}, {
  label: 'Average tenor < 60 days',
  value: '42.3d',
  status: 'pass'
}];
const EXPOSURE: ExposureRow[] = [{
  name: 'Walmart',
  type: 'Obligor',
  pct: 18.2,
  limit: 20,
  status: 'warn'
}, {
  name: 'Amazon',
  type: 'Obligor',
  pct: 12.4,
  limit: 20,
  status: 'pass'
}, {
  name: 'Target',
  type: 'Obligor',
  pct: 10.2,
  limit: 20,
  status: 'pass'
}, {
  name: 'Top 5 obligors',
  type: 'Obligor',
  pct: 52.8,
  limit: 60,
  status: 'watch'
}, {
  name: 'Retail',
  type: 'Sector',
  pct: 31.0,
  limit: 35,
  status: 'warn'
}, {
  name: 'Logistics',
  type: 'Sector',
  pct: 18.5,
  limit: 35,
  status: 'pass'
}];
const ALERTS: AlertRow[] = [{
  time: '09:42',
  level: 'breach',
  message: 'External rating feed revised Walmart outlook to A2'
}, {
  time: '09:38',
  level: 'warn',
  message: 'Retail sector exposure approaching internal 35% cap'
}, {
  time: '09:31',
  level: 'info',
  message: 'Model flagged elevated disputes on Anchor D (7D window)'
}, {
  time: '09:14',
  level: 'info',
  message: 'Rolling 30D collection rate improved to 98.7%'
}];
const ACTIONS: ActionRow[] = [{
  time: '09:40',
  actor: 'risk@mint.us',
  action: 'Submitted Walmart internal limit override for Q2 vintage',
  result: 'Pending committee decision'
}, {
  time: '09:20',
  actor: 'system',
  action: 'Tightened pricing band for Anchor B new tickets',
  result: 'Applied to pipeline'
}, {
  time: '08:55',
  actor: 'committee',
  action: 'Approved diversification guideline for next vintage',
  result: 'Live and monitored'
}];
export function RiskManagement() {
  useEffect(() => {
    document.title = 'Mint | Risk Management';
  }, []);
  return <div className="min-h-screen bg-gray-50" data-id="element-2339">
      {/* Header */}
      <div className="mb-6 flex items-center justify-between" data-id="element-2340">
        <div data-id="element-2341">
          <h1 className="text-3xl font-semibold text-gray-900" data-id="element-2342">
            Risk Management
          </h1>
          <p className="text-sm text-gray-600 mt-1" data-id="element-2343">
            Covenant Compliance and Concentration Limits
          </p>
        </div>
        <div className="flex items-center text-xs text-gray-500" data-id="element-2344">
          <span className="inline-block h-2 w-2 rounded-full bg-emerald-500 mr-2" data-id="element-2345" />
          Live monitoring
        </div>
      </div>
      {/* Top strip: high-level risk KPIs (same grammar as Allocation Engine header) */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-8" data-id="element-2346">
        <KpiTile label="Overall status" value="Compliant" icon={<ShieldIcon size={18} className="text-gray-500" data-id="element-2348" />} data-id="element-2347" />
        <KpiTile label="Active breaches" value="0" icon={<CheckCircleIcon size={18} className="text-gray-500" data-id="element-2350" />} data-id="element-2349" />
        <KpiTile label="Watchlist items" value="3" icon={<AlertTriangleIcon size={18} className="text-gray-500" data-id="element-2352" />} data-id="element-2351" />
        <KpiTile label="Overrides pending" value="1" icon={<ClockIcon size={18} className="text-gray-500" data-id="element-2354" />} data-id="element-2353" />
      </div>
      {/* Main grid */}
      <div className="grid grid-cols-1 xl:grid-cols-3 gap-6 mb-8" data-id="element-2355">
        {/* Left column: limits + exposure */}
        <div className="xl:col-span-2 space-y-6" data-id="element-2356">
          <CardPanel title="Covenant compliance" data-id="element-2357">
            <div className="space-y-3 text-sm" data-id="element-2358">
              {LIMITS.map(row => <LimitRowItem key={row.label} row={row} data-id="element-2359" />)}
            </div>
          </CardPanel>
          <CardPanel title="Concentration and exposure" data-id="element-2360">
            <div className="overflow-x-auto" data-id="element-2361">
              <table className="min-w-full text-xs" data-id="element-2362">
                <thead className="bg-gray-50 text-gray-500 uppercase" data-id="element-2363">
                  <tr data-id="element-2364">
                    <th className="px-3 py-2 text-left" data-id="element-2365">Name</th>
                    <th className="px-3 py-2 text-left" data-id="element-2366">Type</th>
                    <th className="px-3 py-2 text-left" data-id="element-2367">Exposure</th>
                    <th className="px-3 py-2 text-left" data-id="element-2368">Limit</th>
                    <th className="px-3 py-2 text-left" data-id="element-2369">Status</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100" data-id="element-2370">
                  {EXPOSURE.map(row => <tr key={row.name} className="hover:bg-gray-50" data-id="element-2371">
                      <td className="px-3 py-2 text-gray-900" data-id="element-2372">{row.name}</td>
                      <td className="px-3 py-2 text-gray-600" data-id="element-2373">{row.type}</td>
                      <td className="px-3 py-2" data-id="element-2374">
                        <ExposureBar pct={row.pct} data-id="element-2375" />
                      </td>
                      <td className="px-3 py-2 text-gray-600" data-id="element-2376">
                        {row.limit.toFixed(1)}%
                      </td>
                      <td className="px-3 py-2" data-id="element-2377">
                        <StatusBadge status={row.status} data-id="element-2378" />
                      </td>
                    </tr>)}
                </tbody>
              </table>
            </div>
          </CardPanel>
        </div>
        {/* Right column: limits, alerts, decisions */}
        <div className="space-y-6" data-id="element-2379">
          <CardPanel title="Portfolio limits and overrides" data-id="element-2380">
            <div className="space-y-3 text-sm" data-id="element-2381">
              <div className="flex items-start gap-2" data-id="element-2382">
                <AlertTriangleIcon size={16} className="text-gray-500 mt-0.5 flex-shrink-0" data-id="element-2383" />
                <div data-id="element-2384">
                  <div className="text-gray-900" data-id="element-2385">
                    Walmart exposure approaching 20% limit
                  </div>
                  <div className="text-xs text-gray-600 mt-1" data-id="element-2386">
                    Current: 18.2% of AUM. Model projects 19.6% at current
                    pacing.
                  </div>
                  <button className="mt-1 text-xs font-medium text-gray-800 hover:text-gray-900" data-id="element-2387">
                    Submit override request
                  </button>
                </div>
              </div>
              <div className="flex items-start gap-2" data-id="element-2388">
                <InfoIcon size={16} className="text-gray-500 mt-0.5 flex-shrink-0" data-id="element-2389" />
                <div data-id="element-2390">
                  <div className="text-gray-900" data-id="element-2391">Diversification guidance</div>
                  <div className="text-xs text-gray-600 mt-1" data-id="element-2392">
                    Shift next vintage toward logistics and healthcare anchors
                    to relieve retail.
                  </div>
                </div>
              </div>
              <div className="flex items-start gap-2" data-id="element-2393">
                <InfoIcon size={16} className="text-gray-500 mt-0.5 flex-shrink-0" data-id="element-2394" />
                <div data-id="element-2395">
                  <div className="text-gray-900" data-id="element-2396">
                    Next formal review: Thursday 10:00 AM
                  </div>
                  <div className="text-xs text-gray-600 mt-1" data-id="element-2397">
                    Agenda: concentration, tenor drift, sector mix.
                  </div>
                </div>
              </div>
            </div>
          </CardPanel>
          <CardPanel title="Real-time alerts" data-id="element-2398">
            <div className="space-y-2 text-xs max-h-64 overflow-y-auto" data-id="element-2399">
              {ALERTS.map((alert, idx) => <AlertRowItem key={idx} alert={alert} data-id="element-2400" />)}
            </div>
          </CardPanel>
          <CardPanel title="Recent risk decisions" data-id="element-2401">
            <div className="space-y-2 text-xs max-h-64 overflow-y-auto" data-id="element-2402">
              {ACTIONS.map((a, idx) => <div key={idx} className="border-b border-gray-100 pb-2 last:border-b-0 last:pb-0" data-id="element-2403">
                  <div className="flex items-center justify-between mb-1" data-id="element-2404">
                    <span className="text-gray-500" data-id="element-2405">{a.time}</span>
                    <span className="text-gray-700 font-medium" data-id="element-2406">{a.actor}</span>
                  </div>
                  <div className="text-gray-800" data-id="element-2407">{a.action}</div>
                  <div className="text-gray-500 mt-0.5" data-id="element-2408">{a.result}</div>
                </div>)}
            </div>
          </CardPanel>
        </div>
      </div>
    </div>;
}
/* helper components */
function KpiTile({
  label,
  value,
  icon
}: {
  label: string;
  value: string;
  icon?: React.ReactNode;
}) {
  return <div className="bg-white rounded-lg border border-gray-200 p-4 shadow-sm" data-id="element-2409">
      <div className="flex items-start justify-between mb-1" data-id="element-2410">
        <div className="text-[11px] text-gray-500 uppercase tracking-wide" data-id="element-2411">
          {label}
        </div>
        {icon && <div data-id="element-2412">{icon}</div>}
      </div>
      <div className="text-xl font-semibold text-gray-900" data-id="element-2413">{value}</div>
    </div>;
}
function CardPanel({
  title,
  children
}: {
  title: string;
  children: React.ReactNode;
}) {
  return <div className="bg-white rounded-lg border border-gray-200 p-4 shadow-sm" data-id="element-2414">
      <div className="text-xs font-semibold text-gray-700 uppercase mb-3" data-id="element-2415">
        {title}
      </div>
      {children}
    </div>;
}
function LimitRowItem({
  row
}: {
  row: LimitRow;
}) {
  return <div className="flex items-center justify-between border border-gray-100 rounded-md px-3 py-2 bg-gray-50" data-id="element-2416">
      <div className="flex-1" data-id="element-2417">
        <div className="text-xs text-gray-600" data-id="element-2418">{row.label}</div>
        {row.note && <div className="text-[11px] text-gray-500 mt-1" data-id="element-2419">{row.note}</div>}
      </div>
      <div className="flex items-center gap-2" data-id="element-2420">
        <span className="text-sm font-semibold text-gray-900" data-id="element-2421">{row.value}</span>
        <StatusBadge status={row.status} data-id="element-2422" />
      </div>
    </div>;
}
function StatusBadge({
  status
}: {
  status: LimitStatus;
}) {
  const base = 'inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-medium';
  if (status === 'pass') {
    return <span className={`${base} bg-gray-100 text-gray-800`} data-id="element-2423">Pass</span>;
  }
  if (status === 'warn') {
    return <span className={`${base} bg-gray-100 text-gray-800`} data-id="element-2424">Warn</span>;
  }
  return <span className={`${base} bg-gray-900 text-white`} data-id="element-2425">Watch</span>;
}
function ExposureBar({
  pct
}: {
  pct: number;
}) {
  return <div className="flex items-center gap-2" data-id="element-2426">
      <div className="flex-1 h-2 bg-gray-200 rounded-full overflow-hidden" data-id="element-2427">
        <div className="h-full bg-gray-700" style={{
        width: `${Math.min(100, pct)}%`
      }} data-id="element-2428" />
      </div>
      <span className="w-12 text-right text-gray-700" data-id="element-2429">{pct.toFixed(1)}%</span>
    </div>;
}
function AlertRowItem({
  alert
}: {
  alert: AlertRow;
}) {
  const {
    level
  } = alert;
  const icon = level === 'breach' ? <AlertTriangleIcon size={12} className="text-gray-700" data-id="element-2430" /> : level === 'warn' ? <AlertTriangleIcon size={12} className="text-gray-600" data-id="element-2431" /> : <InfoIcon size={12} className="text-gray-500" data-id="element-2432" />;
  return <div className="flex items-start gap-2 border-b border-gray-100 pb-2 last:border-b-0 last:pb-0" data-id="element-2433">
      <span className="text-gray-500 w-10" data-id="element-2434">{alert.time}</span>
      <span data-id="element-2435">{icon}</span>
      <span className="text-gray-800" data-id="element-2436">{alert.message}</span>
    </div>;
}